import React, { useRef, useCallback, useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface EmailViewerProps {
  emailHTML: string;
  mode: "light" | "dark";
}

const EmailViewer: React.FC<EmailViewerProps> = ({ emailHTML, mode }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const textColor = mode === "light" ? "#000000" : "#FFFFFF";

const sanitizedEmail = DOMPurify.sanitize(emailHTML, {
  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'strong', 'em', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'div', 'span', 'a', 'img', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'align', 'valign', 'cellpadding', 'cellspacing'],

});

  const styledHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <base target="_blank">
      <style>
        html, body {
          margin: 0;
          padding: 0;
          color: ${textColor};
          overflow: hidden;
        }
        body {
          padding: 16px;
        }
        div, table {
          background-color: transparent !important;
        }
        img {
          max-width: 100%;
          height: auto;
          display: block;
        }
      </style>
    </head>
    <body>
      ${sanitizedEmail}
    </body>
    </html>
  `;

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;

    const observer = new ResizeObserver(() => {
      if (!iframe.contentDocument) return;
      
      const body = iframe.contentDocument.body;
      const html = iframe.contentDocument.documentElement;
      const newHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.scrollHeight,
        html.offsetHeight
      );

      if (newHeight > 0) {
        iframe.style.height = `${newHeight}px`;
        setIsVisible(true);
      }
    });

    observer.observe(iframe.contentDocument.body);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsVisible(false);
  }, [emailHTML]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={styledHTML}
      onLoad={handleLoad}
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{
        width: "100%",
        border: "none",
        display: "block",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.2s ease-in",
        minHeight: "100px",
      }}
    />
  );
};

export default EmailViewer;